import type * as api from '@gitbook/api-client';
import {
    type FileLocation,
    type FileUploadMetadata,
    type NativeFile,
    uploadFiles as uploadFilesFromDatabase,
} from '@gitbook/app-database';
import { createFile } from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { IntlPluralize } from '@gitbook/react-uikit';
import { type FilesDropZoneAccept, toast } from '@gitbook/spine-react';
import { useLiveRef } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../state';
import { getHumanFileSize, isFileAccepted, promptFile } from '../../utils';

const REVISION_FILE_MAX_SIZE = 100 * 1024 * 1024;

type UploadFileOptions = {
    accept?: FilesDropZoneAccept;
    multiple?: boolean;
};

type UploadFilesAPI = {
    uploadFiles: (
        nativeFiles: NativeFile[],
        options?: UploadFileOptions
    ) => Promise<FileUploadMetadata[]>;
    promptAndUpload: (options?: UploadFileOptions | undefined) => Promise<FileUploadMetadata[]>;
    insertFiles: (filesMetadata: FileUploadMetadata[]) => Promise<api.ContentRefFile[]>;
    uploadAndInsertFiles: (nativeFiles: NativeFile[]) => Promise<api.ContentRefFile[]>;
    uploadAndUpdateFile: (fileId: string, nativeFile: NativeFile) => Promise<void>;
};

/**
 * Return callbacks for uploading files to a location.
 */
export function useUploadFiles() {
    const hiveBranch = useHiveBranch();
    const user = useLoggedInUser();

    /**
     * Upload files to the storage.
     * (This function will NOT insert the files in the branch)
     */
    const uploadFiles: UploadFilesAPI['uploadFiles'] = async (nativeFiles, options) => {
        const { accept = 'files', multiple = true } = options || {};

        assertIsDefined(hiveBranch);
        const location: FileLocation = { kind: 'space_file', space: hiveBranch.contentId };

        const initialFilesCount = nativeFiles.length;
        nativeFiles = nativeFiles.filter((file) => isFileAccepted(file, accept));

        const ignoreCount = initialFilesCount - nativeFiles.length;
        if (ignoreCount > 0) {
            toast.error('Files ignored', null, {
                description: (
                    <>
                        {ignoreCount}{' '}
                        <IntlPluralize
                            count={initialFilesCount - nativeFiles.length}
                            singular="file is"
                            plural="files are"
                        />{' '}
                        not matching the accepted file types.
                    </>
                ),
            });
        }

        if (!multiple && nativeFiles.length > 1) {
            nativeFiles = [nativeFiles[0]];
        }

        const filesToUpload = filterTooLargeFiles(nativeFiles);

        if (filesToUpload.length === 0) {
            return [];
        }

        const promise = uploadFilesFromDatabase(location, filesToUpload);
        toast.promise(promise, {
            loading: `Uploading ${nativeFiles.length} files…`,
            success: 'Files uploaded',
            error: 'Error while uploading your files',
        });
        return promise;
    };

    /**
     * Prompt the user to select files and upload them to the storage.
     * (This function will NOT insert the files in the branch)
     */
    const promptAndUpload: UploadFilesAPI['promptAndUpload'] = async (options = {}) => {
        assertIsDefined(hiveBranch);
        const location: FileLocation = { kind: 'space_file', space: hiveBranch.contentId };
        const nativeFiles = await promptFile({ multiple: true, ...options });

        const filesToUpload = filterTooLargeFiles(nativeFiles);

        if (!filesToUpload.length) {
            return [];
        }

        const promise = uploadFilesFromDatabase(location, filesToUpload);
        toast.promise(promise, {
            loading: `Uploading ${nativeFiles.length} files…`,
            success: 'Files uploaded',
            error: 'Error while uploading your files',
        });
        return promise;
    };

    const insertFiles: UploadFilesAPI['insertFiles'] = async (filesMetadata) => {
        const results: api.ContentRefFile[] = [];
        assertIsDefined(hiveBranch);
        await hiveBranch.change((change) => {
            filesMetadata.forEach((metadata) => {
                const file = createFile(metadata, user.uid);
                results.push({ kind: 'file', file: file.id });
                change.insertFile(file);
            });
        });

        return results;
    };

    /**
     * Upload a list of native files and insert them in the branch revision.
     */
    const uploadAndInsertFiles: UploadFilesAPI['uploadAndInsertFiles'] = async (nativeFiles) => {
        const filesMetadata = await uploadFiles(nativeFiles);
        return insertFiles(filesMetadata);
    };

    /**
     * Upload a native file and update the file in the branch revision.
     */
    const uploadAndUpdateFile: UploadFilesAPI['uploadAndUpdateFile'] = async (
        fileId,
        nativeFile
    ) => {
        assertIsDefined(hiveBranch);

        const existingFile = hiveBranch.revision.files[fileId];
        if (!existingFile) {
            throw new Error(`File ${fileId} not found`);
        }

        const [fileMetadata] = await uploadFiles([nativeFile]);
        await hiveBranch.change((change) => {
            change.updateFile(existingFile.id, fileMetadata);
        });
    };

    return useLiveRef<UploadFilesAPI>({
        uploadFiles,
        uploadAndInsertFiles,
        uploadAndUpdateFile,
        promptAndUpload,
        insertFiles,
    }).current;
}

const filterTooLargeFiles = (files: NativeFile[]): NativeFile[] => {
    const result: NativeFile[] = [];
    const skippedFiles: NativeFile[] = [];

    for (const file of files) {
        if (file.size > REVISION_FILE_MAX_SIZE) {
            skippedFiles.push(file);
        } else {
            result.push(file);
        }
    }

    if (skippedFiles.length > 0) {
        // We limit the message for the file names and use ellipsis if it exceeds 150 characters
        const filesNames = skippedFiles.map((file) => file.name).join(', ');
        const trimmedFilesNames =
            filesNames.length > 150 ? `${filesNames.slice(0, 150)}…` : filesNames;

        toast.error('File too large', null, {
            description: `Your file must be less than ${getHumanFileSize(
                REVISION_FILE_MAX_SIZE
            )}. Skipped files: ${trimmedFilesNames}`,
        });
    }

    return result;
};
